import { Suspense, useMemo } from "react";
import { useGlobalSearchParams } from "expo-router";
import { useTranslation } from "react-i18next";
import { Text, View, XStack } from "tamagui";

import { api } from "~/utils/api";
import {
  ErrorBoundary,
  LoadingScreen,
  PreconditionResearchFallback,
} from "~/components/blocks";
import {
  SlicesFilter,
  SlicesResultsFilterProvider,
  useSlicesResultsFilterContext,
} from "~/components/blocks/filters/slices-filter";
import type { Slices } from "~/components/feature/research";
import {
  LinearChartByScaleAndSurveyType,
  ScoreCard,
  StepsLinearChart,
} from "~/components/feature/research";
import { normalizeScales, SlicesScalesList } from "~/components/feature/scales";

const Results = () => {
  const { researchId, type } = useGlobalSearchParams<{
    type: Slices;
    researchId: string;
  }>();

  return (
    <View gap={"$5"} paddingTop={"$5"}>
      <XStack flex={10} gap="$2" width="100%">
        <Suspense fallback={<LoadingScreen height={500} width={"100%"} />}>
          <StepsLinearChart researchId={researchId} />
          <ScoreCard researchId={researchId} />
        </Suspense>
      </XStack>
      <SlicesResultsFilterProvider researchId={researchId}>
        <ErrorBoundary fallback={PreconditionResearchFallback}>
          <Suspense fallback={<LoadingScreen height={500} width={"100%"} />}>
            <ScalesWithFilter researchId={researchId} type={type} />
          </Suspense>
        </ErrorBoundary>
      </SlicesResultsFilterProvider>
    </View>
  );
};

export default Results;

const ScalesWithFilter: React.FC<{ researchId: string; type: Slices }> = ({
  researchId,
  type,
}) => {
  const { t } = useTranslation("slices");
  const { filter } = useSlicesResultsFilterContext();
  const [stats] = api.phase.getStats.useSuspenseQuery(
    {
      researchId,
      filter: { slice: filter.slice ?? "" },
    },
    {
      select: (data) => {
        return {
          scales: normalizeScales(
            data.scales.map(({ shown: _, ...rest }) => rest),
          ),
        };
      },

      retry: (failureCount) => {
        return failureCount < 1;
      },
    },
  );

  const scales = useMemo(() => {
    return (
      stats?.scales
        .filter((item) => diagnosticAggeregationScales.includes(item.scale))
        .map(({ title, scale }) => ({ name: title, value: scale })) ?? []
    );
  }, [stats?.scales]);

  return (
    <>
      {type === "diagnostic" && (
        <LinearChartByScaleAndSurveyType
          researchId={researchId}
          scales={scales}
        />
      )}
      {stats?.scales ? (
        <XStack alignItems="center" justifyContent={"space-between"}>
          <Text
            fontFamily={"$subtitle"}
            fontWeight={"$6"}
            fontSize={"$7"}
            lineHeight={"$7"}
            color={"$gray-950"}
          >
            {t("results.scales.title")}
          </Text>

          <SlicesFilter />
        </XStack>
      ) : null}
      <SlicesScalesList data={stats?.scales} researchId={researchId} />
    </>
  );
};

const diagnosticAggeregationScales = [
  "gadtotal",
  "phq9",
  "ptsdscore",
  "bipftotal",
  "insomnia",
  "affectivedysregulation",
  "negativeselfconcept",
  "denial",
  "foodaddiction",
  "socialnetworkaddiction",
  "workaddiction",
  "work",
  "angerout",
  "angerin",
];
